import { useState } from "react";
import { toast } from "sonner";
import { Loader2, Save, X } from "lucide-react";
import { SPLIT_FIELDS, money, toCents, reconcileClosing } from "../../../lib/closing";
import { StatusChip, fmtBizDate, matchLabel } from "./closingUi";
import { RegistersTable } from "./RegistersTable";

const Field = ({ label, value, onChange, testid, step = "0.01" }) => (
  <label className="block">
    <span className="text-[10px] font-bold uppercase tracking-[0.14em] text-[color:var(--ups-subtext)]">{label}</span>
    <input type="number" min="0" step={step} value={value} onChange={(e) => onChange(e.target.value)}
      className="input-swiss w-full px-2 py-1.5 mt-0.5 rounded-sm text-[13px] font-mono text-right" data-testid={testid} />
  </label>
);

export const ClosingDetail = ({ closing, onClose, onSaved }) => {
  const rec = closing.reconciliation || {};
  const [form, setForm] = useState(() => Object.fromEntries([...SPLIT_FIELDS.map((f) => [f.key, rec[f.key] ?? ""]), ["customer_count", rec.customer_count ?? ""], ["average_sale", rec.average_sale ?? ""], ["notes", rec.notes || ""]]));
  const [busy, setBusy] = useState(false);
  const t = closing.totals;
  const accounted = SPLIT_FIELDS.reduce((s, f) => s + toCents(form[f.key]), 0) + toCents(t.cash_deposit) + toCents(t.checks_total);
  const diff = accounted - toCents(t.total_sale);
  const customers = parseInt(form.customer_count, 10) || 0;

  const save = async () => {
    setBusy(true);
    try {
      const payload = { ...Object.fromEntries(SPLIT_FIELDS.map((f) => [f.key, Number(form[f.key]) || 0])), customer_count: customers, average_sale: Number(form.average_sale) || 0, notes: form.notes };
      const res = await reconcileClosing(closing.id, payload);
      toast[res.reconciliation.matches ? "success" : "warning"](`Saved — ${matchLabel(Math.round(res.reconciliation.difference * 100))}`);
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="p-4 rounded-sm space-y-4" style={{ background: "#FFF8E1", border: "2px solid #FFB500" }} data-testid="closing-detail">
      <div className="flex items-start justify-between gap-2">
        <div>
          <div className="font-display text-[18px] font-bold">{closing.store_name} · {fmtBizDate(closing.business_date)}</div>
          <div className="text-[12px] text-[color:var(--ups-subtext)]">Closed by {closing.closed_by_name} · {new Date(closing.submitted_at).toLocaleString([], { timeZone: "America/New_York", timeZoneName: "short" })}{closing.notes ? ` · “${closing.notes}”` : ""}</div>
        </div>
        <div className="flex items-center gap-2"><StatusChip rec={closing.reconciliation} />
          <button onClick={onClose} className="p-1.5 rounded-sm hover:bg-white" data-testid="closing-detail-close"><X size={16} /></button></div>
      </div>
      <div className="p-3 rounded-sm bg-white border border-[color:var(--ups-border)]"><RegistersTable closing={closing} onSaved={onSaved} /></div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="p-3 rounded-sm bg-white border border-[color:var(--ups-border)]">
          <div className="text-[10px] font-bold uppercase tracking-[0.14em] text-[color:var(--ups-subtext)] mb-2">Split from the UPS system</div>
          <div className="grid grid-cols-2 gap-3">
            {SPLIT_FIELDS.map((f) => <Field key={f.key} label={f.label} value={form[f.key]} onChange={(v) => setForm({ ...form, [f.key]: v })} testid={`closing-split-${f.key}`} />)}
            <Field label="Customer count" step="1" value={form.customer_count} onChange={(v) => setForm({ ...form, customer_count: v })} testid="closing-split-customer_count" />
            <Field label="Average sale" value={form.average_sale} onChange={(v) => setForm({ ...form, average_sale: v })} testid="closing-split-average_sale" />
          </div>
          <textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Notes" rows={2}
            className="input-swiss w-full px-2 py-1.5 mt-3 rounded-sm text-[13px]" data-testid="closing-split-notes" />
        </div>
        <div className="p-3 rounded-sm bg-white border border-[color:var(--ups-border)] font-mono text-[13px] space-y-1.5">
          <div className="text-[10px] font-sans font-bold uppercase tracking-[0.14em] text-[color:var(--ups-subtext)] mb-2">Tally</div>
          <div className="flex justify-between"><span>Cards + accounts</span><span>{money((accounted - toCents(t.cash_deposit) - toCents(t.checks_total)) / 100)}</span></div>
          <div className="flex justify-between"><span>Cash deposited</span><span>{money(t.cash_deposit)}</span></div>
          <div className="flex justify-between"><span>Checks</span><span>{money(t.checks_total)}</span></div>
          <div className="flex justify-between font-bold border-t border-[color:var(--ups-border)] pt-1.5"><span>Accounted for</span><span data-testid="closing-accounted-total">{money(accounted / 100)}</span></div>
          <div className="flex justify-between font-bold"><span>Total sale (POS A+B+C)</span><span>{money(t.total_sale)}</span></div>
          <div className={`flex justify-between font-bold text-[15px] p-2 rounded-sm ${diff === 0 ? "bg-green-100 text-green-800" : "bg-red-100 text-red-700"}`} data-testid="closing-difference">
            <span>Status</span><span>{matchLabel(diff)}</span>
          </div>
          {rec.reconciled_by && <div className="text-[11px] font-sans text-[color:var(--ups-subtext)]">Last saved by {rec.reconciled_by} · {new Date(rec.reconciled_at).toLocaleString([], { timeZone: "America/New_York", timeZoneName: "short" })}</div>}
          <button onClick={save} disabled={busy} className="btn-brutal w-full mt-3 px-4 py-2 text-[12px] font-sans font-bold uppercase tracking-[0.14em] rounded-sm flex items-center justify-center gap-1.5 disabled:opacity-50" data-testid="closing-save-btn">
            {busy ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Save split
          </button>
        </div>
      </div>
    </div>
  );
};
